import {
  type ChangeEvent,
  type FC,
  useEffect,
  useMemo,
  useState,
} from 'react';
import AppLayout from '@/components/feature/AppLayout';

type ExportTableKey =
  | 'clients'
  | 'leads'
  | 'cases'
  | 'legalEntities'
  | 'caseParties'
  | 'legalEvents'
  | 'tasks'
  | 'payments'
  | 'paymentInstallments'
  | 'legalProducts'
  | 'feeRules'
  | 'documentTemplates'
  | 'generatedDocuments'
  | 'knowledgeItems'
  | 'messageTemplates'
  | 'calendarEvents'
  | 'legalSites'
  | 'officeSettings'
  | 'legalDeadlines'
  | 'legalChecklists'
  | 'legalChecklistItems'
  | 'publications'
  | 'publicationMatches'
  | 'publicationMonitors'
  | 'publicationImportRuns';

type ExportPayload = Record<
  ExportTableKey,
  Array<Record<string, unknown>>
>;

type ExportApiResponse = {
  success: boolean;
  schemaVersion: number;
  exportedAt: string;
  data: ExportPayload;
  counts: Record<ExportTableKey, number>;
  errors: string[];
};

type RestoreApiResponse = {
  success: boolean;
  valid?: boolean;
  restored?: boolean;
  schemaVersion?: number;
  totalTables?: number;
  totalRecords?: number;
  restoredAt?: string;
  error?: string;
  validationErrors?: string[];
};

const tableLabels: Array<{
  key: ExportTableKey;
  label: string;
}> = [
  { key: 'clients', label: 'Clientes' },
  { key: 'leads', label: 'Leads' },
  { key: 'cases', label: 'Casos' },
  { key: 'legalEntities', label: 'Entidades Jurídicas' },
  { key: 'caseParties', label: 'Partes dos Casos' },
  { key: 'legalEvents', label: 'Timeline e Auditoria' },
  { key: 'tasks', label: 'Tarefas' },
  { key: 'payments', label: 'Pagamentos' },
  { key: 'paymentInstallments', label: 'Parcelas' },
  { key: 'legalProducts', label: 'Produtos Jurídicos' },
  { key: 'feeRules', label: 'Regras de Honorários' },
  { key: 'documentTemplates', label: 'Modelos de Documentos' },
  { key: 'generatedDocuments', label: 'Documentos Gerados' },
  { key: 'knowledgeItems', label: 'Base de Conhecimento' },
  { key: 'messageTemplates', label: 'Modelos de Mensagens' },
  { key: 'calendarEvents', label: 'Agenda Interna' },
  { key: 'legalSites', label: 'Sites Jurídicos' },
  { key: 'officeSettings', label: 'Configurações do Escritório' },
  { key: 'legalDeadlines', label: 'Prazos Jurídicos' },
  { key: 'legalChecklists', label: 'Checklists Jurídicos' },
  { key: 'legalChecklistItems', label: 'Itens de Checklist' },
  { key: 'publications', label: 'Publicações' },
  { key: 'publicationMatches', label: 'Vínculos de Publicações' },
  { key: 'publicationMonitors', label: 'Monitores de Publicações' },
  { key: 'publicationImportRuns', label: 'Execuções de Importação' },
];

const emptyExportData = tableLabels.reduce((acc, item) => {
  acc[item.key] = [];
  return acc;
}, {} as ExportPayload);

const emptyCounts = tableLabels.reduce((acc, item) => {
  acc[item.key] = 0;
  return acc;
}, {} as Record<ExportTableKey, number>);

const formatDateTime = (value: string) => {
  if (!value) return '-';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const getRestoreError = (
  payload: RestoreApiResponse,
  fallback: string
) => {
  if (
    Array.isArray(payload.validationErrors) &&
    payload.validationErrors.length > 0
  ) {
    return payload.validationErrors.join(' ');
  }

  return payload.error || fallback;
};

const BackupPage: FC = () => {
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);

  const [exportedAt, setExportedAt] = useState('');
  const [counts, setCounts] =
    useState<Record<ExportTableKey, number>>(emptyCounts);

  const [error, setError] = useState('');

  const [selectedBackup, setSelectedBackup] =
    useState<ExportApiResponse | null>(null);

  const [selectedFileName, setSelectedFileName] = useState('');
  const [isBackupValidated, setIsBackupValidated] = useState(false);
  const [restoreMessage, setRestoreMessage] = useState('');

  const loadExportData = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/export-data');
      const payload = (await response.json()) as ExportApiResponse;

      if (!response.ok || !payload?.success) {
        throw new Error(
          'Não foi possível carregar os dados para exportação.'
        );
      }

      setCounts(payload.counts ?? emptyCounts);
      setExportedAt(
        payload.exportedAt || new Date().toISOString()
      );
    } catch (loadError: any) {
      setError(
        loadError?.message ||
          'Não foi possível carregar os dados para exportação.'
      );

      setCounts(emptyCounts);
      setExportedAt('');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadExportData();
  }, []);

  const summaryRows = useMemo(
    () =>
      tableLabels.map((item) => ({
        key: item.key,
        label: item.label,
        count: counts[item.key] ?? 0,
      })),
    [counts]
  );

  const handleDownloadJson = async () => {
    setIsExporting(true);
    setError('');

    try {
      const response = await fetch('/api/export-data');
      const payload = (await response.json()) as ExportApiResponse;

      if (!response.ok || !payload?.success) {
        throw new Error(
          'Não foi possível gerar um backup íntegro.'
        );
      }

      const exportedPayload: ExportApiResponse = {
        success: true,
        schemaVersion: payload.schemaVersion,
        exportedAt:
          payload.exportedAt || new Date().toISOString(),
        counts: payload.counts ?? counts,
        data: payload.data ?? emptyExportData,
        errors: payload.errors ?? [],
      };

      const blob = new Blob(
        [JSON.stringify(exportedPayload, null, 2)],
        {
          type: 'application/json;charset=utf-8',
        }
      );

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');

      const stamp = new Date(exportedPayload.exportedAt)
        .toISOString()
        .replace(/[:.]/g, '-')
        .slice(0, 19);

      link.href = url;
      link.download = `bm-legal-os-backup-${stamp}.json`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      URL.revokeObjectURL(url);

      setCounts(exportedPayload.counts);
      setExportedAt(exportedPayload.exportedAt);
    } catch (downloadError: any) {
      setError(
        downloadError?.message ||
          'Não foi possível gerar o arquivo JSON.'
      );
    } finally {
      setIsExporting(false);
    }
  };

  const handleBackupFile = async (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];

    setSelectedBackup(null);
    setSelectedFileName('');
    setIsBackupValidated(false);
    setRestoreMessage('');
    setError('');

    if (!file) {
      return;
    }

    try {
      const content = await file.text();
      const parsed = JSON.parse(content) as ExportApiResponse;

      setSelectedBackup(parsed);
      setSelectedFileName(file.name);
      setRestoreMessage(
        'Arquivo carregado. Valide antes de restaurar.'
      );
    } catch {
      setError(
        'O arquivo selecionado não contém um JSON válido.'
      );
    }
  };

  const handleValidateBackup = async () => {
    if (!selectedBackup) {
      return;
    }

    setIsValidating(true);
    setError('');
    setRestoreMessage('');
    setIsBackupValidated(false);

    try {
      const response = await fetch('/api/restore-data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mode: 'validate',
          backup: selectedBackup,
        }),
      });

      const payload =
        (await response.json()) as RestoreApiResponse;

      if (!response.ok || !payload.success || !payload.valid) {
        throw new Error(
          getRestoreError(
            payload,
            'O arquivo de backup não passou na validação.'
          )
        );
      }

      setIsBackupValidated(true);

      setRestoreMessage(
        `Backup válido: ${payload.totalTables ?? 0} tabelas e ${
          payload.totalRecords ?? 0
        } registros.`
      );
    } catch (validationError: any) {
      setError(
        validationError?.message ||
          'Não foi possível validar o backup.'
      );
    } finally {
      setIsValidating(false);
    }
  };

  const handleRestoreBackup = async () => {
    if (!selectedBackup || !isBackupValidated) {
      return;
    }

    const confirmation = window.prompt(
      'ATENÇÃO: esta operação substituirá os dados atuais pelo conteúdo do backup. Digite RESTAURAR para confirmar.'
    );

    if (confirmation !== 'RESTAURAR') {
      setRestoreMessage(
        'Restauração cancelada. Nenhum dado foi alterado.'
      );
      return;
    }

    setIsRestoring(true);
    setError('');
    setRestoreMessage('');

    try {
      const response = await fetch('/api/restore-data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mode: 'restore',
          confirm: 'RESTAURAR',
          backup: selectedBackup,
        }),
      });

      const payload =
        (await response.json()) as RestoreApiResponse;

      if (!response.ok || !payload.success || !payload.restored) {
        throw new Error(
          getRestoreError(
            payload,
            'A restauração não pôde ser concluída.'
          )
        );
      }

      setRestoreMessage(
        `Restauração concluída: ${
          payload.totalRecords ?? 0
        } registros restaurados.`
      );

      setIsBackupValidated(false);

      await loadExportData();
    } catch (restoreError: any) {
      setError(
        restoreError?.message ||
          'A restauração não pôde ser concluída.'
      );
    } finally {
      setIsRestoring(false);
    }
  };

  return (
    <AppLayout>
      <div className="p-4 md:p-6 space-y-5">
        <div>
          <h2 className="text-xl font-bold text-foreground-900">
            Backup
          </h2>

          <p className="text-sm text-foreground-500 mt-0.5">
            Exporte, valide e restaure uma cópia de segurança dos
            dados reais do sistema.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="bg-background-50 border border-background-200 rounded-xl p-4">
            <p className="text-[10px] text-foreground-400 uppercase tracking-wider">
              Última exportação
            </p>

            <p className="text-base font-bold text-foreground-900 mt-1">
              {exportedAt
                ? formatDateTime(exportedAt)
                : 'Nenhuma ainda'}
            </p>

            <p className="text-xs text-foreground-500 mt-0.5">
              {loading
                ? 'Carregando dados...'
                : 'JSON manual sob demanda'}
            </p>
          </div>

          <div className="bg-background-50 border border-background-200 rounded-xl p-4">
            <p className="text-[10px] text-foreground-400 uppercase tracking-wider">
              Tabelas incluídas
            </p>

            <p className="text-base font-bold text-foreground-900 mt-1">
              {tableLabels.length}
            </p>

            <p className="text-xs text-foreground-500 mt-0.5">
              Estrutura integral atualmente suportada
            </p>
          </div>

          <div className="bg-background-50 border border-background-200 rounded-xl p-4">
            <p className="text-[10px] text-foreground-400 uppercase tracking-wider">
              Status
            </p>

            <p className="text-base font-bold text-foreground-900 mt-1">
              {error ? 'Atenção' : 'Pronto'}
            </p>

            <p
              className={`text-xs mt-0.5 ${
                error
                  ? 'text-amber-600'
                  : 'text-emerald-600'
              }`}
            >
              {error ||
                'Backup e restauração disponíveis'}
            </p>
          </div>
        </div>

        <div className="bg-background-50 border border-background-200 rounded-xl p-5 space-y-4">
          <div>
            <h3 className="text-sm font-bold text-foreground-900">
              Exportar backup
            </h3>

            <p className="text-xs text-foreground-500 mt-1">
              O arquivo somente é gerado se todas as tabelas forem
              lidas corretamente.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleDownloadJson}
              disabled={isExporting || loading}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-500 px-3 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
            >
              {isExporting ? (
                <i className="ri-loader-4-line animate-spin" />
              ) : (
                <i className="ri-download-2-line" />
              )}

              Baixar JSON
            </button>

            <button
              onClick={loadExportData}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-lg border border-background-200 px-3 py-2 text-sm font-medium text-foreground-600 transition-colors hover:bg-background-100 disabled:opacity-60"
            >
              {loading ? (
                <i className="ri-loader-4-line animate-spin" />
              ) : (
                <i className="ri-refresh-line" />
              )}

              Atualizar resumo
            </button>
          </div>
        </div>

        <div className="bg-background-50 border border-background-200 rounded-xl p-5 space-y-4">
          <div>
            <h3 className="text-sm font-bold text-foreground-900">
              Restaurar backup
            </h3>

            <p className="text-xs text-foreground-500 mt-1">
              Selecione um backup JSON. O sistema valida versão,
              tabelas, contagens, colunas e estrutura antes de
              permitir a restauração.
            </p>
          </div>

          <input
            type="file"
            accept=".json,application/json"
            onChange={handleBackupFile}
            className="block w-full text-sm text-foreground-600 file:mr-3 file:rounded-lg file:border-0 file:bg-background-100 file:px-3 file:py-2 file:text-sm file:font-medium file:text-foreground-700 hover:file:bg-background-200"
          />

          {selectedFileName && (
            <div className="rounded-lg border border-background-200 bg-background-100 px-3 py-2">
              <p className="text-xs text-foreground-600">
                Arquivo selecionado:{' '}
                <strong>{selectedFileName}</strong>
              </p>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleValidateBackup}
              disabled={
                !selectedBackup ||
                isValidating ||
                isRestoring
              }
              className="inline-flex items-center gap-2 rounded-lg border border-primary-300 px-3 py-2 text-sm font-semibold text-primary-600 transition-colors hover:bg-primary-50 disabled:opacity-50"
            >
              {isValidating ? (
                <i className="ri-loader-4-line animate-spin" />
              ) : (
                <i className="ri-shield-check-line" />
              )}

              Validar arquivo
            </button>

            <button
              onClick={handleRestoreBackup}
              disabled={
                !selectedBackup ||
                !isBackupValidated ||
                isRestoring ||
                isValidating
              }
              className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-40"
            >
              {isRestoring ? (
                <i className="ri-loader-4-line animate-spin" />
              ) : (
                <i className="ri-history-line" />
              )}

              Restaurar backup
            </button>
          </div>

          {restoreMessage && (
            <div
              className={`rounded-lg border px-3 py-2 ${
                isBackupValidated
                  ? 'border-emerald-200 bg-emerald-50'
                  : 'border-background-200 bg-background-100'
              }`}
            >
              <p className="text-xs text-foreground-700">
                {restoreMessage}
              </p>
            </div>
          )}

          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-xs text-amber-800">
              A restauração substitui os dados atuais. Ela só é
              liberada após validação do arquivo e exige que seja
              digitada a confirmação RESTAURAR.
            </p>
          </div>
        </div>

        <div className="bg-background-50 border border-background-200 rounded-xl p-5 space-y-4">
          <div className="overflow-hidden rounded-xl border border-background-200">
            <table className="w-full">
              <thead className="bg-background-100">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-foreground-500">
                    Tabela
                  </th>

                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-foreground-500">
                    Registros
                  </th>
                </tr>
              </thead>

              <tbody>
                {summaryRows.map((row) => (
                  <tr
                    key={row.key}
                    className="border-t border-background-100"
                  >
                    <td className="px-3 py-2 text-sm text-foreground-700">
                      {row.label}
                    </td>

                    <td className="px-3 py-2 text-right text-sm font-medium text-foreground-900">
                      {row.count}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rounded-xl border border-dashed border-background-300 bg-background-50 px-4 py-3">
            <p className="text-xs text-foreground-500">
              Os arquivos de backup podem conter dados pessoais,
              processuais e financeiros sensíveis. Mantenha-os em
              local seguro.
            </p>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default BackupPage;
